import type {
  AgeGroup,
  AvailabilityEditor,
  AvailabilitySlot,
  Booking,
  Schedule,
  WaitlistEntry,
} from './types';

export type AssistantSchedule = Schedule | 'Full time and part time';
export type AvailabilityOperation = 'set' | 'add';
export type AvailabilityDraftField = 'ageGroup' | 'count' | 'schedule' | 'startDate';

export interface AvailabilityDraft {
  ageGroup: AgeGroup | null;
  count: number | null;
  schedule: AssistantSchedule | null;
  startDate: string | null;
  operation: AvailabilityOperation;
}

export interface AvailabilityProposal {
  ageGroup: AgeGroup;
  count: number;
  previousCount: number;
  nextCount: number;
  schedule: AssistantSchedule;
  startDate: string;
  operation: AvailabilityOperation;
  note: string;
}

export interface WaitlistDraft {
  parentName: string | null;
  childAge: AgeGroup | null;
  phone: string | null;
}

export interface HoursNoticeProposal {
  dateLabel: string;
  message: string;
}

const EMPTY_DRAFT: AvailabilityDraft = {
  ageGroup: null,
  count: null,
  schedule: null,
  startDate: null,
  operation: 'set',
};
const EMPTY_WAITLIST_DRAFT: WaitlistDraft = {
  parentName: null,
  childAge: null,
  phone: null,
};

const NUMBER_WORDS: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};

const AGE_PATTERNS: Array<[AgeGroup, RegExp]> = [
  ['Young Toddler', /\byoung\s+toddlers?\b/i],
  ['Older Toddler', /\bolder\s+toddlers?\b/i],
  ['Pre-K', /\b(?:pre[\s-]?k|preschool)\b/i],
  ['School Age', /\bschool[\s-]?age\b/i],
  ['Infant', /\binfants?\b/i],
];

const formatStartDate = (value: string): string => {
  const trimmed = value.trim().replace(/[.,!?]+$/, '');
  if (/^(?:now|immediately|today|right away)$/i.test(trimmed)) return 'Available now';
  return `Starts ${trimmed}`;
};

const extractAgeGroup = (message: string): AgeGroup | null => {
  for (const [group, pattern] of AGE_PATTERNS) {
    if (pattern.test(message)) return group;
  }
  if (/\btoddlers?\b/i.test(message)) return null;
  return null;
};

const extractCount = (message: string): number | null => {
  const describesAvailability = /\b(?:openings?|spots?|availabilit(?:y|ies))\b/i.test(message);
  const digitMatch =
    message.match(/\b(\d{1,2})\s+(?:openings?|spots?|availabilit(?:y|ies))\b/i) ??
    message.match(/^\s*(\d{1,2})\s*$/);
  if (digitMatch) return Number(digitMatch[1]);
  if (describesAvailability) {
    const firstNumber = message.match(/\b(\d{1,2})\b/);
    if (firstNumber) return Number(firstNumber[1]);
  }

  const lower = message.toLowerCase();
  for (const [word, value] of Object.entries(NUMBER_WORDS)) {
    if (describesAvailability && new RegExp(`\\b${word}\\b`).test(lower)) {
      return value;
    }
  }
  return null;
};

const extractSchedule = (message: string): AssistantSchedule | null => {
  if (
    /\b(?:both|full[\s-]?time\s+(?:and|&)\s+part[\s-]?time|part[\s-]?time\s+(?:and|&)\s+full[\s-]?time)\b/i.test(
      message,
    )
  ) {
    return 'Full time and part time';
  }
  if (/\bfull[\s-]?time\b/i.test(message)) return 'Full time';
  if (/\bpart[\s-]?time\b/i.test(message)) return 'Part time';
  return null;
};

const extractStartDate = (message: string): string | null => {
  const dateMatch = message.match(
    /\b(?:starting|starts?|available|from)\s+(now|immediately|today|right away|[a-z]+\s+\d{1,2}(?:st|nd|rd|th)?(?:,\s*\d{4})?|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)\b/i,
  );
  if (dateMatch) return formatStartDate(dateMatch[1]);
  if (/^(?:now|immediately|today|right away)[.!]?$/i.test(message.trim())) {
    return 'Available now';
  }
  return null;
};

export const createAvailabilityDraft = (): AvailabilityDraft => ({ ...EMPTY_DRAFT });

export const interpretAvailabilityMessage = (
  message: string,
  current: AvailabilityDraft = EMPTY_DRAFT,
): AvailabilityDraft => {
  const ageGroup = extractAgeGroup(message);
  const count = extractCount(message);
  const schedule = extractSchedule(message);
  const startDate = extractStartDate(message);
  const operation = /\b(?:add|increase\s+by)\b/i.test(message) ? 'add' : current.operation;

  return {
    ageGroup: ageGroup ?? current.ageGroup,
    count: count ?? current.count,
    schedule: schedule ?? current.schedule,
    startDate: startDate ?? current.startDate,
    operation,
  };
};

export const getNextMissingField = (draft: AvailabilityDraft): AvailabilityDraftField | null => {
  if (!draft.ageGroup) return 'ageGroup';
  if (draft.count === null) return 'count';
  if (!draft.schedule) return 'schedule';
  if (!draft.startDate) return 'startDate';
  return null;
};

export const getClarifyingQuestion = (field: AvailabilityDraftField): string => {
  switch (field) {
    case 'ageGroup':
      return 'Which age group is this for: infant, young toddler, older toddler, Pre-K, or school age?';
    case 'count':
      return 'How many openings should families see?';
    case 'schedule':
      return 'Are these openings full-time, part-time, or both?';
    case 'startDate':
      return 'When do these openings start? You can reply with a date or say “available now.”';
  }
};

export const buildAvailabilityProposal = (
  draft: AvailabilityDraft,
  currentSlot: AvailabilitySlot,
): AvailabilityProposal => {
  if (!draft.ageGroup || draft.count === null || !draft.schedule || !draft.startDate) {
    throw new Error('Availability draft is incomplete');
  }

  const nextCount = draft.operation === 'add' ? currentSlot.spots + draft.count : draft.count;
  const note = `${draft.schedule} · ${draft.startDate}`;

  return {
    ageGroup: draft.ageGroup,
    count: draft.count,
    previousCount: currentSlot.spots,
    nextCount,
    schedule: draft.schedule,
    startDate: draft.startDate,
    operation: draft.operation,
    note,
  };
};

export const buildAvailabilityUpdate = (
  proposal: AvailabilityProposal,
): Partial<AvailabilityEditor> => ({
  [proposal.ageGroup]: {
    on: proposal.nextCount > 0,
    count: proposal.nextCount,
    note: proposal.note,
  },
});

export const findTourToConfirm = (message: string, bookings: Booking[]): Booking | null => {
  if (!/\bconfirm\b/i.test(message) || !/\b(?:tour|visit|appointment)\b/i.test(message)) {
    return null;
  }

  const pending = bookings.filter((booking) => booking.status === 'pending');
  const normalized = message.toLowerCase();
  const namedMatch = pending.find((booking) => {
    const firstName = booking.parentName.split(/\s+/)[0]?.toLowerCase();
    return firstName ? normalized.includes(firstName) : false;
  });

  if (namedMatch) return namedMatch;
  return pending.length === 1 ? pending[0] : null;
};

const extractPhone = (message: string): string | null => {
  const digits = message.replace(/\D/g, '');
  if (digits.length !== 10) return null;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const createWaitlistDraft = (): WaitlistDraft => ({ ...EMPTY_WAITLIST_DRAFT });

export const interpretWaitlistMessage = (
  message: string,
  current: WaitlistDraft = EMPTY_WAITLIST_DRAFT,
): WaitlistDraft | null => {
  const isWaitlistRequest = /\bwaitlist\b/i.test(message);
  if (!isWaitlistRequest && !current.parentName) return null;

  const parentMatch = message.match(
    /\badd\s+(?:the\s+)?(.+?)\s+to\s+(?:the\s+)?(?:infant|young\s+toddlers?|older\s+toddlers?|pre[\s-]?k|preschool|school[\s-]?age)\s+waitlist\b/i,
  );

  return {
    parentName: parentMatch?.[1]?.trim() || current.parentName,
    childAge: extractAgeGroup(message) ?? current.childAge,
    phone: extractPhone(message) ?? current.phone,
  };
};

export const getWaitlistQuestion = (draft: WaitlistDraft): string | null => {
  if (!draft.parentName) return 'What family name should I add to the waitlist?';
  if (!draft.childAge) return 'Which age group should I add them to?';
  if (!draft.phone) return 'What phone number should the center use for this family?';
  return null;
};

export const interpretHoursNotice = (
  message: string,
  now: Date = new Date(),
): HoursNoticeProposal | null => {
  if (!/\bclos(?:e|ing)\b/i.test(message)) return null;

  const dayMatch = message.match(/\b(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i);
  const timeMatch = message.match(/\b(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/i);
  if (!dayMatch || !timeMatch) return null;

  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const targetDay = weekdays.findIndex((day) => day.toLowerCase() === dayMatch[1].toLowerCase());
  const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const daysAhead = (targetDay - date.getUTCDay() + 7) % 7;
  date.setUTCDate(date.getUTCDate() + daysAhead);

  const hour = Number(timeMatch[1]);
  const minutes = timeMatch[2] || '00';
  const period = timeMatch[3]?.toUpperCase() || (hour >= 1 && hour <= 6 ? 'PM' : 'AM');
  const dateLabel = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);

  return {
    dateLabel,
    message: `Closing at ${hour}:${minutes} ${period}`,
  };
};

export const findNextWaitlistEntry = (
  message: string,
  entries: WaitlistEntry[],
): WaitlistEntry | null => {
  if (
    !/\b(?:contact|text|message|notify)\b/i.test(message) ||
    !/\b(?:waitlist|family)\b/i.test(message)
  ) {
    return null;
  }

  const ageGroup = extractAgeGroup(message);
  const waiting = entries.filter(
    (entry) => entry.status === 'waiting' && (!ageGroup || entry.childAge === ageGroup),
  );
  return waiting[0] ?? null;
};

export const findWaitlistPlacement = (
  message: string,
  entries: WaitlistEntry[],
): WaitlistEntry | null => {
  if (!/\b(?:accepted|enrolled|placed|took the spot)\b/i.test(message)) return null;

  const contacted = entries.filter((entry) => entry.status === 'contacted');
  const normalized = message.toLowerCase();
  const namedMatch = contacted.find((entry) => {
    const nameParts = entry.parentName.toLowerCase().split(/\s+/);
    return nameParts.some(
      (part) => part.length > 2 && part !== 'family' && normalized.includes(part),
    );
  });
  return namedMatch ?? (contacted.length === 1 ? contacted[0] : null);
};
